<template>
  <div class="d-flex flex-column">
    <module-navigation-bar title="Module Assembly">
      <template #module-nav-logo>
        <img
          v-if="isDaiwaLicense"
          alt=""
          height="40px"
          src="https://www.daiwahousemodular.eu/wp-content/uploads/daiwa_logo.svg"
        />
      </template>
    </module-navigation-bar>
    <div
      v-if="status === 'success'"
      class="d-flex"
      style="height: calc(100% - 46px)"
    >
      <div
        class="py-5 white ant-border-right d-flex flex-column overflow-hidden"
        style="min-width: 250px; max-width: 300px"
      >
        <div class="d-flex align-center mb-5 px-5 flex-grow-0">
          Assembly Hall
          <v-spacer />
          <v-chip class="ml-2">{{ hall.hall }}</v-chip>
        </div>

        <!-- Scrollable area -->
        <div class="overflow-y-auto pr-2 flex-grow-1 min-h-0">
          <v-list>
            <v-list-item-group
              v-model="focusedLocationIndex"
              color="primary"
              mandatory
            >
              <v-list-item
                v-for="location in locations"
                :key="location.id"
                class="d-flex"
                three-line
              >
                <div class="mr-4 d-flex align-center justify-center">
                  {{ location.order }}
                </div>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ location.assembly_location }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ location.description }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle>
                    <v-chip
                      v-for="type in getTypesByLocation(location.id)"
                      :key="type.id"
                      x-small
                    >
                      {{ type.type }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </div>
      </div>
      <div class="pa-5 d-flex flex-grow-1">
        <dynamic-data-table
          :is-loading="tableIsLoading"
          :items-per-page="-1"
          :table-headers="elementHeaders"
          :table-records="records"
          auto-sort-column="module_type"
          class="background-white flex-grow-1"
          table-title="Elements"
        >
          <template #table-actions>
            <v-combobox
              v-model="filteredModuleTypes"
              :items="uniqueModuleTypes"
              chips
              class="mr-2"
              deletable-chips
              dense
              filled
              hide-details
              multiple
              placeholder="Module type"
            />
            <v-menu
              v-model="generateMenu"
              :close-on-content-click="false"
              bottom
              nudge-bottom="35"
            >
              <template #activator="{ on, attrs }">
                <v-btn color="primary" elevation="0" v-bind="attrs" v-on="on"
                  >Generate
                </v-btn>
              </template>

              <v-card width="500">
                <v-list>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title
                        >This action will generate the sequence based on the
                        position
                      </v-list-item-title>
                      <v-list-item-subtitle
                        >For example position 2.1 will result in sequence 21
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <v-divider></v-divider>

                <v-list>
                  <v-list-item>
                    <v-list-item-action>
                      <v-switch
                        v-model="generateAndOverwrite"
                        color="primary"
                      ></v-switch>
                    </v-list-item-action>
                    <v-list-item-title
                      >{{
                        generateAndOverwrite
                          ? 'Generate/overwrite all values'
                          : 'Generate only empty values'
                      }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-action>
                      <v-checkbox v-model="generateDoubleCheck" />
                    </v-list-item-action>
                    <v-list-item-title
                      >Check this box when you are ready
                    </v-list-item-title>
                  </v-list-item>
                </v-list>

                <v-card-actions>
                  <v-spacer></v-spacer>

                  <v-btn
                    :disabled="isGenerating"
                    color="error"
                    text
                    @click="closeGenerateMenu"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    :disabled="!generateDoubleCheck || isGenerating"
                    :loading="isGenerating"
                    color="primary"
                    elevation="0"
                    @click="generateByPosition"
                  >
                    Generate
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </template>
          <template #item.module_type="{ item }">
            <td
              class="text-decoration-underline text-primary c-pointer"
              @click="fetchDrawing(item.module_type)"
            >
              <v-icon
                v-if="item.module_type === previewType"
                class="mr-2"
                color="primary"
                small
                >mdi-circle
              </v-icon>
              {{ item.module_type }}
            </td>
          </template>
          <template #item.assembly_sequence="{ item }">
            <div class="editable-field">
              <v-edit-dialog
                :return-value.sync="item.assembly_sequence"
                @save="saveSequence(item)"
              >
                {{ item.assembly_sequence }}
                <template #input>
                  <v-text-field
                    v-model="item.assembly_sequence"
                    :min="0"
                    placeholder="Sequence"
                    single-line
                    type="number"
                  />
                </template>
              </v-edit-dialog>
              <v-icon class="edit-icon" small> mdi-pencil</v-icon>
            </div>
          </template>
        </dynamic-data-table>

        <iframe
          v-if="documentPreview"
          :src="documentPreview"
          class="ml-15"
          height="100%"
          style="max-width: 50%"
          type="application/pdf"
          width="50%"
        />
        `
      </div>
    </div>
    <div v-else class="d-flex justify-center align-center full-height">
      <ant-loading />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ModuleNavigationBar from '@/components/Modules/ModuleNavigationBar.vue';
import AntLoading from '@/components/AntLoading.vue';
import DynamicDataTable from '@/components/DynamicDataTable.vue';
import { bookOfObjects } from '@/services/bookOf';
import {
  createRecord,
  getRecordDocument,
  importRecords,
  updateRecord,
} from '@/services/api/record.api';
import { queryTablesV2 } from '@/services/api/v2/connect_v2';

export default {
  name: 'DhmeModuleAssembly',
  components: {
    DynamicDataTable,
    AntLoading,
    ModuleNavigationBar,
  },
  data: () => {
    return {
      focusedLocationIndex: null,
      tableRecords: [],
      elementHeaders: [
        { text: 'Module Type', value: 'module_type', hasSlot: true },
        { text: 'Type', value: 'element_category' },
        { text: 'Position', value: 'element_position' },
        { text: 'Sequence', value: 'assembly_sequence', hasSlot: true },
      ],
      elementSequenceTableId: null,
      tableIsLoading: false,
      generateMenu: false,
      generateAndOverwrite: false,
      generateDoubleCheck: false,
      isGenerating: false,

      filteredModuleTypes: [],
      documentPreview: null,
      previewType: null,
    };
  },
  computed: {
    ...mapGetters({
      project: 'project',
      status: 'dhmeModuleAssembly/moduleStatus',
      locations: 'dhmeModuleAssembly/locations',
      types: 'dhmeModuleAssembly/locationTypes',
      hall: 'dhmeModuleAssembly/assemblyHall',
      isDaiwaLicense: 'isDaiwaLicense',
    }),

    focusedLocation() {
      return this.locations[this.focusedLocationIndex];
    },

    uniqueModuleTypes() {
      return [...new Set(this.tableRecords.map((r) => r.module_type))];
    },

    records() {
      if (this.filteredModuleTypes.length > 0) {
        return this.tableRecords.filter((r) =>
          this.filteredModuleTypes.includes(r.module_type)
        );
      }
      return this.tableRecords;
    },
  },
  watch: {
    focusedLocation(value) {
      if (value) {
        this.getElementsByLocationType();
      }
    },
  },
  async mounted() {
    await this.$store.dispatch('dhmeModuleAssembly/fetchModuleAssemblyData');
  },
  methods: {
    async fetchDrawing(type) {
      this.tableIsLoading = true;
      const { modules } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_MODULES',
            project: this.project.id,
            as: 'modules',
            columns: [
              {
                name: 'module_type',
                conditions: [
                  {
                    operator: '=',
                    value: type,
                  },
                ],
              },
              {
                name: '2d_drawing',
              },
            ],
          },
        ],
      });

      for (const r of modules.records) {
        if (r['2d_drawing']) {
          let file = await getRecordDocument(
            r['2d_drawing'].id,
            this.project.id,
            modules.id
          );

          if (file.file) {
            this.documentPreview =
              URL.createObjectURL(
                this.b64toBlob(file.file, 'application/pdf')
              ) + '#navpanes=0&scrollbar=0&view=FitV';
            this.previewType = r.module_type;
            this.tableIsLoading = false;
            return;
          }
        }
      }
      this.$store.commit('showNotification', {
        content: '2D drawing not found for this module type',
        color: 'info',
      });
      this.tableIsLoading = false;
    },
    b64toBlob(b64Data, contentType) {
      const byteCharacters = atob(b64Data);

      let byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        let slice = byteCharacters.slice(offset, offset + 512),
          byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        let byteArray = new Uint8Array(byteNumbers);

        byteArrays.push(byteArray);
      }
      return new Blob(byteArrays, { type: contentType });
    },
    async generateByPosition() {
      this.isGenerating = true;
      let records = this.generateAndOverwrite
        ? this.tableRecords
        : this.tableRecords.filter((x) => !x.assembly_sequence);

      let data = records.map((r) => {
        return {
          id: r.id,
          module_type: r.module_type,
          element_position: r.element_position,
          assembly_sequence: parseInt(r.element_position.replace(/\./g, '')),
        };
      });

      let book = bookOfObjects('records', data);
      let csv = book.convert('csv', 'string');
      let parsedCsv = btoa(unescape(encodeURIComponent(csv)));

      await importRecords({
        project: {
          id: this.project.id,
        },
        table: {
          id: this.elementSequenceTableId,
        },
        records: parsedCsv,
      });

      await this.getElementsByLocationType();
      this.isGenerating = false;
      this.closeGenerateMenu();
    },
    closeGenerateMenu() {
      this.generateMenu = false;
      this.generateDoubleCheck = false;
      this.generateAndOverwrite = false;
    },
    getTypesByLocation(locationId) {
      return this.types.filter((t) => t.assembly_location === locationId);
    },
    async saveSequence(item) {
      this.tableIsLoading = true;
      let body = {
        assembly_sequence: item.assembly_sequence
          ? parseInt(item.assembly_sequence)
          : null,
      };
      if (item.id) {
        // update record
        let updatedItem = await updateRecord(item.id, {
          project: { id: this.project.id },
          table: { id: this.elementSequenceTableId },
          record: body,
        });
        Object.assign(
          this.tableRecords.find((r) => r.id === updatedItem.id),
          updatedItem
        );
      } else {
        body.module_type = item.module_type;
        body.element_position = item.element_position;
        // create record
        let newItem = await createRecord({
          project: { id: this.project.id },
          table: { id: this.elementSequenceTableId },
          record: body,
        });
        let x = this.tableRecords.find(
          (r) =>
            r.module_type === newItem.module_type &&
            r.element_position === newItem.element_position
        );
        Object.assign(x, {
          id: newItem.id,
          type: newItem.module_type,
          element_category: item.element_category,
          element_position: newItem.element_position,
          assembly_sequence: newItem.assembly_sequence,
        });
      }
      this.tableIsLoading = false;
    },
    async getElementsByLocationType() {
      this.tableIsLoading = true;
      const { elements } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_ELEMENTS',
            project: this.project.id,
            as: 'elements',
            orderBy: 'element_position',
            columns: [
              {
                name: 'element_category',
                conditions: [
                  {
                    operator: '=',
                    values: this.getTypesByLocation(
                      this.focusedLocation.id
                    ).map((t) => t.type),
                  },
                ],
              },
              {
                name: 'module_type',
              },
              {
                name: 'element_position',
              },
            ],
          },
        ],
      });

      const { sequences } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_ELEMENTS_SEQUENCE',
            project: this.project.id,
            as: 'sequences',
            orderBy: 'module_type',
            columns: [
              {
                name: 'element_position',
                conditions: [
                  {
                    operator: '=',
                    values: elements.records.map((e) => e.element_position),
                  },
                ],
              },
              {
                name: 'module_type',
                conditions: [
                  {
                    operator: '=',
                    values: elements.records.map((e) => e.module_type),
                  },
                ],
              },
              {
                name: 'assembly_sequence',
              },
            ],
          },
        ],
      });

      this.elementSequenceTableId = sequences.id;

      let tableRecords = [];
      elements.records.forEach((e) => {
        if (
          !tableRecords.find(
            (r) =>
              r.module_type === e.module_type &&
              r.element_position === e.element_position
          )
        ) {
          let sequence = sequences.records.find(
            (s) =>
              e.module_type === s.module_type &&
              e.element_position === s.element_position
          );
          tableRecords.push({
            ...e,
            assembly_sequence: sequence?.assembly_sequence ?? null,
            id: sequence?.id ?? null,
          });
        }
      });

      tableRecords.sort((a, b) => {
        // Compare module_type
        if (a.module_type < b.module_type) return -1;
        if (a.module_type > b.module_type) return 1;

        // If module_type is the same, compare assembly_sequence
        if (a.assembly_sequence < b.assembly_sequence) return -1;
        if (a.assembly_sequence > b.assembly_sequence) return 1;

        // If assembly_sequence is the same, compare element_position
        return a.element_position - b.element_position;
      });

      this.tableRecords = tableRecords;
      this.tableIsLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.min-h-0 {
  min-height: 0;
}
</style>
